'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Achievement {
  metric: string;
  value: string;
  description: string;
}

interface Role {
  id: number;
  title: string;
  company: string;
  location: string;
  period: string;
  duration: string;
  industry: string;
  type: string;
  description: string;
  keyResponsibilities: string[];
  achievements: Achievement[];
  skills: string[];
  impact: {
    financial?: string;
    operational?: string;
    compliance?: string;
    team?: string;
  };
}

interface RoleCardProps {
  role: Role;
}

const RoleCard: React.FC<RoleCardProps> = ({ role }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getIndustryColor = (industry: string) => {
    switch (industry.toLowerCase()) {
      case 'logistics':
        return 'bg-teal-100 text-teal-800 border-teal-200';
      case 'environmental':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'oil & gas':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'telecommunications':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl shadow-subtle hover:shadow-elevated transition-all duration-300">
      <div className="p-6 lg:p-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
          <div className="flex-1">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="BriefcaseIcon" size={24} className="text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl lg:text-2xl font-heading font-bold text-foreground mb-1">
                  {role.title}
                </h3>
                <p className="text-lg font-semibold text-primary mb-2">
                  {role.company}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-sm text-text-secondary">
                  <span className="flex items-center gap-1">
                    <Icon name="MapPinIcon" size={16} />
                    {role.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="CalendarIcon" size={16} />
                    {role.period}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="ClockIcon" size={16} />
                    {role.duration}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <span
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${getIndustryColor(
                role.industry
              )}`}
            >
              {role.industry}
            </span>
            <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-muted text-text-primary border border-border">
              {role.type}
            </span>
          </div>
        </div>

        <p className="text-text-primary leading-relaxed mb-6">
          {role.description}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {role.achievements.slice(0, 4).map((achievement, index) => (
            <div
              key={index}
              className="bg-muted rounded-lg p-4 border border-border"
            >
              <div className="text-2xl font-heading font-bold text-primary mb-1">
                {achievement.value}
              </div>
              <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
                {achievement.metric}
              </div>
              <div className="text-sm text-text-primary">
                {achievement.description}
              </div>
            </div>
          ))}
        </div>

        {isExpanded && (
          <div className="space-y-6 pt-6 border-t border-border">
            <div>
              <h4 className="text-lg font-heading font-bold text-foreground mb-4 flex items-center gap-2">
                <Icon name="ChartBarIcon" size={20} className="text-accent" />
                Business Impact
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {role.impact.financial && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon
                        name="CurrencyDollarIcon"
                        size={20}
                        className="text-blue-600"
                      />
                      <span className="font-semibold text-blue-900">
                        Financial Impact
                      </span>
                    </div>
                    <p className="text-sm text-blue-800">{role.impact.financial}</p>
                  </div>
                )}
                {role.impact.operational && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon
                        name="CogIcon"
                        size={20}
                        className="text-green-600"
                      />
                      <span className="font-semibold text-green-900">
                        Operational Impact
                      </span>
                    </div>
                    <p className="text-sm text-green-800">
                      {role.impact.operational}
                    </p>
                  </div>
                )}
                {role.impact.compliance && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon
                        name="ShieldCheckIcon"
                        size={20}
                        className="text-purple-600"
                      />
                      <span className="font-semibold text-purple-900">
                        Compliance Impact
                      </span>
                    </div>
                    <p className="text-sm text-purple-800">
                      {role.impact.compliance}
                    </p>
                  </div>
                )}
                {role.impact.team && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon
                        name="UsersIcon"
                        size={20}
                        className="text-amber-600"
                      />
                      <span className="font-semibold text-amber-900">
                        Team Impact
                      </span>
                    </div>
                    <p className="text-sm text-amber-800">{role.impact.team}</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h4 className="text-lg font-heading font-bold text-foreground mb-4 flex items-center gap-2">
                <Icon name="CheckCircleIcon" size={20} className="text-accent" />
                Key Responsibilities
              </h4>
              <ul className="space-y-2">
                {role.keyResponsibilities.map((responsibility, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-text-primary"
                  >
                    <Icon
                      name="ChevronRightIcon"
                      size={20}
                      className="text-primary flex-shrink-0 mt-0.5"
                    />
                    <span>{responsibility}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-heading font-bold text-foreground mb-4 flex items-center gap-2">
                <Icon name="AcademicCapIcon" size={20} className="text-accent" />
                Skills Utilized
              </h4>
              <div className="flex flex-wrap gap-2">
                {role.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium border border-primary/20"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all duration-300"
        >
          <span>{isExpanded ? 'Show Less' : 'View Full Details'}</span>
          <Icon
            name="ChevronDownIcon"
            size={20}
            className={`transform transition-transform duration-300 ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default RoleCard;